<template>
    <div class="logistics-personal-stats">
        <!-- Заголовок и описание системы -->
        <div v-if="!hideMotivationText" class="motivation-header mb-4">
            <div class="card">
                <div class="card-body">
                    <h5 class="card-title">Система мотивации отдела логистики</h5>
                    <p class="card-text">
                        Система построена на простом и справедливом принципе: 
                        <strong>«Чем больше денег ты сэкономил компании, тем больше получишь сам»</strong>.
                    </p>
                    <p class="card-text">
                        Вы не просто выполняете рутинную работу по поиску перевозчиков, а напрямую влияете на финансовый результат компании. 
                        Ваша находчивость, умение договариваться и эффективно работать напрямую отражаются на вашей зарплате.
                    </p>
                </div>
            </div>
        </div>

        <!-- Лоадер -->
        <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Загрузка...</span>
            </div>
            <p class="mt-2">Загрузка персональной статистики...</p>
        </div>

        <!-- Персональная статистика -->
        <div v-else>

            <!-- Расчет бонуса -->
            <div class="bonus-calculation mb-4">
                <div class="card">
                    <div class="card-header">
                        <h6 class="mb-0">
                            <template v-if="!hideMotivationText">Расчет вашего бонуса</template>
                            <template v-else>Расчет бонуса</template>
                        </h6>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col-md-4">
                                <div class="calculation-step">
                                    <h6>Шаг 1: Эффективность</h6>
                                    <p class="text-muted">Успешные поставки / Всего запросов</p>
                                    <div class="calculation-result">
                                        <span class="h4 font-weight-bold">{{ personalStats.successfulDeliveries }} / {{ personalStats.totalRequests }} = {{ personalStats.efficiency }}%</span>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="calculation-step">
                                    <h6>Шаг 2: Маржинальность</h6>
                                    <p class="text-muted">Сумма сэкономленных средств</p>
                                    <div class="calculation-result">
                                        <span class="h4 text-success font-weight-bold">{{ formatCurrency(personalStats.margin) }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="calculation-step">
                                    <h6>Шаг 3: Сверхдоход</h6>
                                    <p class="text-muted">Маржа − ЗП (и прочие затраты компании)</p>
                                    <div class="calculation-result">
                                        <span class="h4 text-info font-weight-bold">{{ formatCurrency(personalStats.superIncome) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <hr>
                        <div class="final-bonus text-center">
                            <h5>Рассчитанный бонус: <span class="text-success h3 font-weight-bold">{{ formatCurrency(personalStats.bonus) }}</span></h5>
                            <p class="text-muted">Сверхдоход × Бонусный процент ({{ personalStats.bonusPercentage }}%)</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Таблица бонусов по эффективности -->
            <div class="bonus-table mb-4">
                <div class="card">
                    <div class="card-header">
                        <h6 class="mb-0">Бонусные проценты по эффективности</h6>
                    </div>
                    <div class="card-body">
                        <div class="table-responsive">
                            <table class="table table-striped">
                                <thead>
                                    <tr>
                                        <th style="height: 46px; width: 230px;">Диапазон эффективности</th>
                                        <th class="text-center" style="height: 46px; width: 25%;">Бонусный процент</th>
                                        <th style="height: 46px;">
                                            <template v-if="!hideMotivationText">Ваш статус</template>
                                            <template v-else>Статус</template>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="bonus in bonusRanges" :key="bonus.range" 
                                        :class="{ 'table-success': bonus.isCurrent }">
                                        <td>{{ bonus.range }}</td>
                                        <td class="text-center"><strong>{{ bonus.percentage }}%</strong></td>
                                        <td>
                                            <span v-if="bonus.isCurrent" class="badge badge-success">Текущий</span>
                                            <span v-else class="badge badge-secondary">-</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Таблица расчета эффективности -->
            <div class="efficiency-calculation-table mb-4">
                <div class="card">
                    <div class="card-header">
                        <h6 class="mb-0">Расчет эффективности</h6>
                    </div>
                    <div class="card-body">
                        <div class="table-responsive">
                            <table class="table table-striped">
                                <thead>
                                    <tr>
                                        <th style="height: 46px; width: 230px;">Показатель</th>
                                        <th class="text-center" style="height: 46px; width: 25%;">Количество</th>
                                        <th style="height: 46px;">Описание</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><strong>Успешные поставки</strong></td>
                                        <td class="text-center">
                                            <span 
                                                class="clickable-number text-success font-weight-bold"
                                                :class="{ 'has-orders': personalStats.successfulDeliveries > 0 }"
                                                @click="showOrdersModal('successful')"
                                                :title="`Показать ${personalStats.successfulDeliveries} успешных поставок`"
                                            >
                                                {{ personalStats.successfulDeliveries }}
                                            </span>
                                        </td>
                                        <td>Заявки, перешедшие в поставку</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Отказные заявки</strong></td>
                                        <td class="text-center">
                                            <span 
                                                class="clickable-number text-danger font-weight-bold"
                                                :class="{ 'has-orders': personalStats.rejectedRequests > 0 }"
                                                @click="showOrdersModal('rejected')"
                                                :title="`Показать ${personalStats.rejectedRequests} отказных заявок`"
                                            >
                                                {{ personalStats.rejectedRequests }}
                                            </span>
                                        </td>
                                        <td>Заявки и поставки, которые были отклонены</td>
                                    </tr>
                                    <tr class="table-info">
                                        <td><strong>Всего заявок</strong></td>
                                        <td class="text-center">
                                            <span 
                                                class="clickable-number text-primary font-weight-bold"
                                                :class="{ 'has-orders': personalStats.totalRequests > 0 }"
                                                @click="showOrdersModal('all')"
                                                :title="`Показать ${personalStats.totalRequests} всех заявок`"
                                            >
                                                {{ personalStats.totalRequests }}
                                            </span>
                                        </td>
                                        <td>Успешные + Отказные</td>
                                    </tr>
                                    <tr class="table-success">
                                        <td><strong>Эффективность</strong></td>
                                        <td class="text-center text-success font-weight-bold h4">{{ personalStats.efficiency }}%</td>
                                        <td>Успешные / Всего × 100%</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Расчет маржинальности -->
            <div class="marginality-calculation-table mb-4">
                <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-center">
                        <div>
                            <h6 class="mb-0">Расчет маржинальности</h6>
                            <small class="text-muted d-block mt-1">
                                Маржа = Сумма всех статей расходов (в рублях по курсу ЦБ + процент) - Реальные затраты
                            </small>
                        </div>
                        <b-button 
                            v-if="detailedCalculations.length > 0"
                            variant="outline-info" 
                            size="sm" 
                            @click="showMarginBreakdown = !showMarginBreakdown"
                        >
                            <i class="fas" :class="showMarginBreakdown ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                            {{ showMarginBreakdown ? 'Скрыть' : 'Показать' }} расшифровку
                        </b-button>
                    </div>
                    <div class="card-body">
                        <!-- Лоадер для детальных расчетов -->
                        <div v-if="detailedCalculationsLoading" class="text-center py-3">
                            <div class="spinner-border spinner-border-sm text-primary" role="status">
                                <span class="sr-only">Загрузка...</span>
                            </div>
                            <p class="mt-2 mb-0 text-muted">Загрузка детальных расчетов...</p>
                        </div>
                        
                        <!-- Таблица детальных расчетов с пагинацией -->
                        <div v-else-if="detailedCalculations.length > 0">
                            <b-table
                                :items="detailedCalculations"
                                :fields="detailedCalculationsFields"
                                :per-page="perPage"
                                :current-page="currentPage"
                                striped
                                small
                                hover
                                responsive
                                :busy="detailedCalculationsLoading"
                                class="detailed-calculations-table"
                            >
                                <!-- Слот для заказа -->
                                <template #cell(order_id)="row">
                                    <span 
                                        class="sidebar-link text-primary font-weight-bold cursor-pointer" 
                                        @click="openInNewWindow ? openOrderInNewWindow(row.item.order_id) : openOrderSidebar(row.item.order_id, '')" 
                                        :title="openInNewWindow ? 'Открыть заказ в новой вкладке' : 'Открыть заказ'"
                                    >
                                        #{{ row.item.order_id }}
                                    </span>
                                </template>

                                <!-- Слот для итоговой суммы статей расходов -->
                                <template #cell(delivery_cost)="row">
                                    <span class="text-secondary font-weight-bold" :title="'Итоговая сумма всех статей расходов в рублях (рассчитана по курсу ЦБ + процент)'">
                                        {{ formatCurrency(row.item.delivery_cost) }}
                                    </span>
                                </template>

                                <!-- Слот для реальной ставки -->
                                <template #cell(real_rate_total)="row">
                                    <span class="text-primary font-weight-bold">
                                        {{ formatCurrency(row.item.real_rate_total) }}
                                    </span>
                                </template>

                                <!-- Слот для маржи -->
                                <template #cell(margin)="row">
                                    <span 
                                        class="font-weight-bold"
                                        :class="row.item.is_positive_margin ? 'text-success' : 'text-danger'"
                                    >
                                        {{ formatCurrency(row.item.margin) }}
                                    </span>
                                </template>

                                <!-- Слот для статуса -->
                                <template #cell(is_positive_margin)="row">
                                    <span 
                                        class="badge"
                                        :class="row.item.is_positive_margin ? 'badge-success' : (Number(row.item.margin) === 0 ? 'badge-secondary' : 'badge-danger')"
                                    >
                                        {{ row.item.is_positive_margin ? 'Прибыль' : (Number(row.item.margin) === 0 ? 'Нулевая маржа' : 'Убыток') }}
                                    </span>
                                </template>

                                <!-- Слот для даты расчета -->
                                <template #cell(calculated_at)="row">
                                    <small class="text-muted">
                                        {{ formatDate(row.item.calculated_at) }}
                                    </small>
                                </template>

                                <!-- Слот для загрузки -->
                                <template #table-busy>
                                    <div class="text-center text-primary my-2">
                                        <b-spinner class="align-middle"></b-spinner>
                                        <strong> Загрузка...</strong>
                                    </div>
                                </template>
                            </b-table>

                            <!-- Пагинация -->
                            <div class="d-flex justify-content-between align-items-center mt-3">
                                <div class="d-flex align-items-center">
                                    <span class="text-muted mr-2">Показать:</span>
                                    <b-form-select
                                        v-model="perPage"
                                        :options="perPageOptions"
                                        size="sm"
                                        style="width: 80px;"
                                        class="mr-3"
                                    ></b-form-select>
                                    <span class="text-muted">
                                        {{ (currentPage - 1) * perPage + 1 }}-{{ Math.min(currentPage * perPage, detailedCalculations.length) }} 
                                        из {{ detailedCalculations.length }}
                                    </span>
                                </div>
                                
                                <b-pagination
                                    v-model="currentPage"
                                    :total-rows="detailedCalculations.length"
                                    :per-page="perPage"
                                    size="sm"
                                    class="mb-0"
                                ></b-pagination>
                            </div>
                        </div>
                        
                        <!-- Сообщение если нет данных -->
                        <div v-else class="text-center py-4">
                            <i class="fas fa-chart-line fa-2x text-muted mb-2"></i>
                            <p class="text-muted mb-0">Нет данных о детальных расчетах за выбранный период</p>
                        </div>

                        <!-- Расшифровка маржинальности -->
                        <div v-if="showMarginBreakdown && detailedCalculations.length > 0" class="margin-breakdown mt-4 pt-4 border-top">
                            <h6 class="mb-3">Расшифровка маржинальности</h6>
                            <div v-if="marginBreakdownLoading" class="text-center py-3">
                                <div class="spinner-border spinner-border-sm text-primary" role="status">
                                    <span class="sr-only">Загрузка...</span>
                                </div>
                                <p class="mt-2 mb-0 text-muted">Загрузка расшифровки...</p>
                            </div>
                            <div v-else-if="marginBreakdown.length > 0">
                                <div class="mb-3">
                                    <p class="text-muted mb-2">
                                        <strong>Итого маржа:</strong> 
                                        <span class="text-success font-weight-bold">{{ formatCurrency(totalMarginFromBreakdown) }}</span>
                                    </p>
                                    <p class="text-muted mb-0 small">
                                        <i class="fas fa-info-circle"></i> 
                                        Курс ЦБ и тип конвертации указаны для каждого заказа в таблице ниже
                                    </p>
                                </div>
                                <b-table
                                    :items="marginBreakdownWithTotal"
                                    :fields="marginBreakdownFields"
                                    striped
                                    small
                                    hover
                                    responsive
                                    class="margin-breakdown-table"
                                >
                                    <!-- Слот для заказа -->
                                    <template #cell(order_id)="row">
                                        <span v-if="row.item.order_id !== 'Итого'" 
                                            class="sidebar-link text-primary font-weight-bold cursor-pointer" 
                                            @click="openInNewWindow ? openOrderInNewWindow(row.item.order_id) : openOrderSidebar(row.item.order_id, '')"
                                        >
                                            Заказ #{{ row.item.order_id }}
                                        </span>
                                        <span v-else class="font-weight-bold">
                                            {{ row.item.order_id }}
                                        </span>
                                    </template>

                                    <!-- Слот для курса ЦБ и типа конвертации -->
                                    <template #cell(cbr_info)="row">
                                        <div v-if="row.item.order_id !== 'Итого'" class="cbr-info-cell">
                                            <div class="small">
                                                <strong>Курс ЦБ:</strong> 
                                                <span v-if="row.item.cbr_rate">{{ row.item.cbr_rate.toFixed(4) }}</span>
                                                <span v-else class="text-muted">не указан</span>
                                            </div>
                                            <div v-if="row.item.cbr_rate_date" class="small text-muted">
                                                ({{ formatDateShort(row.item.cbr_rate_date) }})
                                            </div>
                                            <div class="small mt-1">
                                                <strong>Тип:</strong> 
                                                <span v-if="row.item.conversion_type === 'cb_plus_percent' && row.item.conversion_rate">
                                                    ЦБ + {{ row.item.conversion_rate }}%
                                                </span>
                                                <span v-else>
                                                    ЦБ
                                                </span>
                                            </div>
                                        </div>
                                        <span v-else class="text-muted">-</span>
                                    </template>

                                    <!-- Слот для суммы в рублях -->
                                    <template #cell(total_cost_rub)="row">
                                        <span class="font-weight-bold" :class="row.item.order_id === 'Итого' ? '' : 'text-secondary'">
                                            {{ formatCurrency(row.item.total_cost_rub) }}
                                        </span>
                                    </template>

                                    <!-- Слот для реальных затрат -->
                                    <template #cell(real_rate_total)="row">
                                        <span class="font-weight-bold" :class="row.item.order_id === 'Итого' ? '' : 'text-primary'">
                                            {{ formatCurrency(row.item.real_rate_total) }}
                                        </span>
                                    </template>

                                    <!-- Слот для маржи -->
                                    <template #cell(margin)="row">
                                        <span 
                                            class="font-weight-bold"
                                            :class="row.item.order_id === 'Итого' 
                                                ? (row.item.margin >= 0 ? 'text-success' : 'text-danger')
                                                : (row.item.is_positive_margin ? 'text-success' : 'text-danger')"
                                        >
                                            {{ formatCurrency(row.item.margin) }}
                                        </span>
                                    </template>
                                </b-table>
                                <div class="mt-3">
                                    <h6 class="mb-2">Детализация по статьям расходов:</h6>
                                    <b-table
                                        :items="marginBreakdown"
                                        :fields="marginBreakdownDetailsFields"
                                        striped
                                        small
                                        hover
                                        responsive
                                        bordered
                                        class="margin-breakdown-details-table"
                                    >
                                        <!-- Слот для заказа -->
                                        <template #cell(order_id)="row">
                                            <span 
                                                class="sidebar-link text-primary font-weight-bold cursor-pointer" 
                                                @click="openInNewWindow ? openOrderInNewWindow(row.item.order_id) : openOrderSidebar(row.item.order_id, '')"
                                            >
                                                #{{ row.item.order_id }}
                                            </span>
                                        </template>

                                        <!-- Слоты для всех статей расходов -->
                                        <template #cell(delivery_cost_rub)="row">
                                            {{ formatCurrency(row.item.delivery_cost_rub || 0) }}
                                        </template>
                                        <template #cell(auto_pickup_cost_rub)="row">
                                            {{ formatCurrency(row.item.auto_pickup_cost_rub || 0) }}
                                        </template>
                                        <template #cell(pickup_sender_cost_rub)="row">
                                            {{ formatCurrency(row.item.pickup_sender_cost_rub || 0) }}
                                        </template>
                                        <template #cell(customs_clearance_ex1_rub)="row">
                                            {{ formatCurrency(row.item.customs_clearance_ex1_rub || 0) }}
                                        </template>
                                        <template #cell(export_license_rub)="row">
                                            {{ formatCurrency(row.item.export_license_rub || 0) }}
                                        </template>
                                        <template #cell(airport_forwarding_cost_rub)="row">
                                            {{ formatCurrency(row.item.airport_forwarding_cost_rub || 0) }}
                                        </template>
                                        <template #cell(terminal_expenses_cost_rub)="row">
                                            {{ formatCurrency(row.item.terminal_expenses_cost_rub || 0) }}
                                        </template>
                                        <template #cell(rail_delivery_cost_rub)="row">
                                            {{ formatCurrency(row.item.rail_delivery_cost_rub || 0) }}
                                        </template>
                                        <template #cell(port_terminal_expenses_cost_rub)="row">
                                            {{ formatCurrency(row.item.port_terminal_expenses_cost_rub || 0) }}
                                        </template>
                                        <template #cell(warehouse_services_cost_rub)="row">
                                            {{ formatCurrency(row.item.warehouse_services_cost_rub || 0) }}
                                        </template>
                                        <template #cell(total_cost_rub)="row">
                                            <span class="font-weight-bold">{{ formatCurrency(row.item.total_cost_rub || 0) }}</span>
                                        </template>
                                    </b-table>
                                </div>
                            </div>
                            <div v-else class="text-center py-4">
                                <i class="fas fa-info-circle fa-2x text-muted mb-2"></i>
                                <p class="text-muted mb-0">Нет данных для расшифровки маржинальности</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Графики с табами -->
            <div class="charts-section mb-4">
                <div class="card bdr-tabs">
                    <div class="card-header">
                        <ul class="nav nav-tabs card-header-tabs" role="tablist">
                            <li class="nav-item">
                                <a class="nav-link" 
                                :class="{ active: activeChartTab === 'efficiency' }"
                                @click="switchChartTab('efficiency')"
                                href="#"
                                role="tab">
                                    <i class="fas fa-chart-line mr-2"></i>
                                    <template v-if="!hideMotivationText">Твоя эффективность</template>
                                    <template v-else>Эффективность</template>
                                </a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link" 
                                :class="{ active: activeChartTab === 'marginality' }"
                                @click="switchChartTab('marginality')"
                                href="#"
                                role="tab">
                                    <i class="fas fa-ruble-sign mr-2"></i>
                                    <template v-if="!hideMotivationText">Твоя маржинальность</template>
                                    <template v-else>Маржинальность</template>
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div class="card-body">
                        <!-- График эффективности -->
                        <div v-if="activeChartTab === 'efficiency'" class="chart-container">
                            <MotivationEfficiencyChart 
                                :key="`efficiency-${chartKey}`"
                                :rawChartData="efficiencyChartData"
                                :loading="efficiencyChartLoading"
                                title="Эффективность (%)"
                            />
                        </div>

                        <!-- График маржинальности -->
                        <div v-if="activeChartTab === 'marginality'" class="chart-container">
                            <MotivationMarginalityChart 
                                :key="`marginality-${chartKey}`"
                                :rawChartData="marginalityChartData"
                                :loading="marginalityChartLoading"
                                title="Маржинальность (₽)"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Советы по увеличению дохода для сотрудников -->
            <div v-if="!hideMotivationText" class="tips-section mb-4">
                <div class="card">
                    <div class="card-header">
                        <h6 class="mb-0">Как увеличить доход</h6>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col-md-6">
                                <div class="tip-item">
                                    <h6 class="text-primary">Увеличивайте эффективность</h6>
                                    <p>Старайтесь успешно закрывать как можно больше заявок. Каждая неудачная заявка — это упущенная возможность получить бонус.</p>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="tip-item">
                                    <h6 class="text-success">Увеличивайте маржинальность</h6>
                                    <p>Ищите самые выгодные ставки, договаривайтесь с перевозчиками, используйте свои знания рынка.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- OrderList компонент -->
        <OrderList
            v-if="showOrderList"
            :openOrderList="showOrderList"
            :orderList="selectedOrders"
            :label="ordersModalTitle"
        />
    </div>
</template>

<script>
import { 
    API_DEPARTMENT_EFFICIENCY_DATA,
    API_MOTIVATION2025_LOGISTICS,
    // Логистика API
    API_MOTIVATION2025_CHARTS_EFFICIENCY,
    API_MOTIVATION2025_CHARTS_MARGINALITY,
    API_MOTIVATION2025_DETAILED_CALCULATIONS
} from '@/constants/api'

import MotivationEfficiencyChart from '@components/charts/MotivationEfficiencyChart.vue'
import MotivationMarginalityChart from '@components/charts/MotivationMarginalityChart.vue'
import OrderList from '@/components/orders/OrderList.vue'
import { openOrderSidebar } from '@/mixins/openOrderSidebar'

export default {
    name: 'LogisticsPersonalStats',
    components: {
        MotivationEfficiencyChart,
        MotivationMarginalityChart,
        OrderList
    },
    mixins: [openOrderSidebar],
    props: {
        userId: {
            type: Number,
            required: true
        },
        period: {
            type: String,
            default: 'current'
        },
        hideMotivationText: {
            type: Boolean,
            default: false
        },
        openInNewWindow: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            loading: false,
            activeChartTab: 'efficiency',
            efficiencyChartLoading: false,
            marginalityChartLoading: false,
            efficiencyChartData: [],
            marginalityChartData: [],
            chartKey: 0, // Ключ для принудительного перерендера графиков
            currentPeriod: '', // Локальная переменная для периода
            bonusRanges: [
                { range: 'от 10% до 30%', percentage: 7, isCurrent: false },
                { range: 'от 30% до 50%', percentage: 10, isCurrent: true },
                { range: 'от 50% до 70%', percentage: 13, isCurrent: false },
                { range: 'от 70% до 100%', percentage: 15, isCurrent: false }
            ],
            personalStats: {
                efficiency: 42,
                margin: 900000,
                superIncome: 600000,
                bonus: 60000,
                bonusPercentage: 10,
                totalRequests: 100,
                successfulDeliveries: 42,
                rejectedRequests: 35,
                inProgress: 23,
                budgetAmount: 15000000,
                actualCosts: 14100000,
                savings: 900000,
                averageSavingsPerRequest: 21428
            },
            // Данные для OrderList компонента
            selectedOrders: [],
            showOrderList: false,
            ordersModalTitle: '',
            // Данные для детальных расчетов маржинальности
            detailedCalculations: [],
            detailedCalculationsLoading: false,
            // Пагинация для таблицы детальных расчетов
            currentPage: 1,
            perPage: 10,
            perPageOptions: [
                { value: 10, text: '10' },
                { value: 25, text: '25' },
                { value: 50, text: '50' },
                { value: 100, text: '100' }
            ],
            // Флаг для отображения расшифровки маржинальности
            showMarginBreakdown: false,
            marginBreakdown: [],
            marginBreakdownLoading: false,
            marginBreakdownWatcher: null,
            // Поля для таблицы расшифровки маржинальности
            marginBreakdownFields: [
                {
                    key: 'order_id',
                    label: 'Заказ',
                    sortable: true,
                    thClass: 'text-left',
                    tdClass: 'text-left'
                },
                {
                    key: 'cbr_info',
                    label: 'Курс ЦБ / Тип конвертации',
                    sortable: false,
                    thClass: 'text-center',
                    tdClass: 'text-center'
                },
                {
                    key: 'total_cost_rub',
                    label: 'Сумма в рублях',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'real_rate_total',
                    label: 'Реальные затраты',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'margin',
                    label: 'Маржа',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                }
            ],
            // Поля для детализации по статьям расходов
            marginBreakdownDetailsFields: [
                {
                    key: 'order_id',
                    label: 'Заказ',
                    sortable: true,
                    thClass: 'text-left',
                    tdClass: 'text-left'
                },
                {
                    key: 'delivery_cost_rub',
                    label: 'Международная доставка',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'auto_pickup_cost_rub',
                    label: 'Автовывоз до клиента',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'pickup_sender_cost_rub',
                    label: 'Автовывоз от отправителя',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'customs_clearance_ex1_rub',
                    label: 'Экспортная декларация',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'export_license_rub',
                    label: 'Экспортная лицензия',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'airport_forwarding_cost_rub',
                    label: 'Экспедирование в аэропорту',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'terminal_expenses_cost_rub',
                    label: 'Терминальные расходы',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'rail_delivery_cost_rub',
                    label: 'ЖД-доставка',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'port_terminal_expenses_cost_rub',
                    label: 'Терминальные расходы в порту',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'warehouse_services_cost_rub',
                    label: 'Услуги СВХ',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'total_cost_rub',
                    label: 'Итого',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                }
            ],
            // Поля для таблицы детальных расчетов
            detailedCalculationsFields: [
                {
                    key: 'order_id',
                    label: 'Заказ',
                    sortable: true,
                    thClass: 'text-left',
                    tdClass: 'text-left'
                },
                {
                    key: 'delivery_cost',
                    label: 'Итого статей расходов (₽)',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'real_rate_total',
                    label: 'Реальная ставка',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'margin',
                    label: 'Маржа',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'is_positive_margin',
                    label: 'Статус',
                    sortable: true,
                    thClass: 'text-center',
                    tdClass: 'text-center'
                },
                {
                    key: 'calculated_at',
                    label: 'Дата расчета',
                    sortable: true,
                    thClass: 'text-left',
                    tdClass: 'text-left'
                }
            ]
        }
    },
    computed: {
        // Используем computed свойство для периода
        periodValue() {
            return this.currentPeriod || this.period
        },
        // Итоговые суммы из расшифровки маржинальности
        totalCostFromBreakdown() {
            return this.marginBreakdown.reduce((sum, item) => sum + (item.total_cost_rub || 0), 0)
        },
        totalRealRateFromBreakdown() {
            return this.marginBreakdown.reduce((sum, item) => sum + (item.real_rate_total || 0), 0)
        },
        totalMarginFromBreakdown() {
            return this.marginBreakdown.reduce((sum, item) => sum + (item.margin || 0), 0)
        },
        // Данные о курсе ЦБ из расшифровки (берем из первого элемента, так как все используют один курс)
        marginBreakdownCbrRate() {
            return this.marginBreakdown.length > 0 ? this.marginBreakdown[0].cbr_rate : null
        },
        marginBreakdownCbrDate() {
            return this.marginBreakdown.length > 0 ? this.marginBreakdown[0].cbr_rate_date : null
        },
        marginBreakdownConversionType() {
            return this.marginBreakdown.length > 0 ? this.marginBreakdown[0].conversion_type : null
        },
        marginBreakdownConversionRate() {
            return this.marginBreakdown.length > 0 ? this.marginBreakdown[0].conversion_rate : null
        },
        // Данные для таблицы расшифровки с итоговой строкой
        marginBreakdownWithTotal() {
            const items = [...this.marginBreakdown]
            // Добавляем итоговую строку
            if (items.length > 0) {
                items.push({
                    order_id: 'Итого',
                    cbr_info: null,
                    total_cost_rub: this.totalCostFromBreakdown,
                    real_rate_total: this.totalRealRateFromBreakdown,
                    margin: this.totalMarginFromBreakdown,
                    is_positive_margin: this.totalMarginFromBreakdown >= 0,
                    _rowVariant: 'info',
                    _cellVariants: {
                        order_id: 'info',
                        cbr_info: 'info',
                        total_cost_rub: 'info',
                        real_rate_total: 'info',
                        margin: 'info'
                    }
                })
            }
            return items
        }
    },
    watch: {
        // Сбрасываем пагинацию при изменении данных
        detailedCalculations() {
            this.currentPage = 1
        },
        // Сбрасываем пагинацию при изменении количества записей на странице
        perPage() {
            this.currentPage = 1
        },
        // Можно добавить watchers если понадобится
    },
    mounted() {
        // Инициализируем квартал если не задан
        if (!this.period || this.period === 'current') {
            this.currentPeriod = this.getCurrentQuarter()
        } else {
            this.currentPeriod = this.period
        }
        
        // Загружаем данные при старте страницы
        this.loadPersonalStats()
        
        // Загружаем данные для графиков с небольшой задержкой
        this.$nextTick(() => {
            this.loadChartData()
        })
    },
    methods: {
        formatCurrency(amount) {
            return new Intl.NumberFormat('ru-RU', {
                style: 'currency',
                currency: 'RUB',
                minimumFractionDigits: 0
            }).format(amount)
        },
        formatDate(dateString) {
            const date = new Date(dateString)
            return date.toLocaleDateString('ru-RU', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        },
        formatDateShort(dateString) {
            if (!dateString) return ''
            const date = new Date(dateString)
            return date.toLocaleDateString('ru-RU', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            })
        },
        loadPersonalStats() {
            // Загружаем персональную статистику с сервера
            this.loadPersonalMotivationData()
            // Загружаем данные для графиков
            this.loadChartData()
            // Загружаем детальные расчеты маржинальности
            this.loadDetailedCalculations()
            // Загружаем расшифровку маржинальности
            this.loadMarginBreakdown()
        },
        loadPersonalMotivationData() {
            this.loading = true
            
            const params = {
                quarter: this.periodValue,
                department_id: 3, // Отдел логистики
                user_id: this.userId
            }
            
            axios.get(API_MOTIVATION2025_LOGISTICS, { params })
                .then(response => {
                    if (response.data.success && response.data.data.length > 0) {
                        this.processPersonalData(response.data.data[0])
                    } else {
                        console.error('API Error:', response.data.message)
                    }
                })
                .catch(error => {
                    console.error('Error loading personal motivation data:', error)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        processPersonalData(data) {
            // Обновляем персональную статистику
            this.personalStats = {
                efficiency: data.efficiency.percentage,
                margin: data.margin.total_margin,
                superIncome: data.super_income,
                bonus: data.bonus,
                bonusPercentage: data.bonus_percentage,
                totalRequests: data.efficiency.total_deliveries,
                successfulDeliveries: data.efficiency.successful_deliveries,
                rejectedRequests: data.efficiency.total_deliveries - data.efficiency.successful_deliveries,
                inProgress: 0, // Можно добавить в API
                budgetAmount: 0, // Можно добавить в API
                actualCosts: 0, // Можно добавить в API
                savings: data.margin.total_margin,
                averageSavingsPerRequest: data.margin.average_margin_per_order
            }
            
            // Обновляем статус бонусных диапазонов
            this.updateBonusRanges(data.efficiency.percentage)
        },
        updateBonusRanges(efficiency) {
            this.bonusRanges.forEach(bonus => {
                bonus.isCurrent = false
            })
            
            if (efficiency >= 10 && efficiency < 30) {
                this.bonusRanges[0].isCurrent = true
            } else if (efficiency >= 30 && efficiency < 50) {
                this.bonusRanges[1].isCurrent = true
            } else if (efficiency >= 50 && efficiency < 70) {
                this.bonusRanges[2].isCurrent = true
            } else if (efficiency >= 70 && efficiency <= 100) {
                this.bonusRanges[3].isCurrent = true
            }
        },
        getCurrentQuarter() {
            const now = new Date()
            const year = now.getFullYear()
            const month = now.getMonth() + 1
            const minYear = 2025
            const minQuarter = 4 // Q4 2025 - минимальный квартал
            
            let currentQuarter = ''
            
            // Финансовая схема кварталов (год начинается с декабря):
            // Q1: декабрь, январь, февраль (12,1,2)
            // Q2: март, апрель, май (3,4,5)
            // Q3: июнь, июль, август (6,7,8)
            // Q4: сентябрь, октябрь, ноябрь (9,10,11)
            if (month === 12 || month === 1 || month === 2) {
                currentQuarter = month === 12 ? `${year + 1}-Q1` : `${year}-Q1`
            } else if (month >= 3 && month <= 5) {
                currentQuarter = `${year}-Q2`
            } else if (month >= 6 && month <= 8) {
                currentQuarter = `${year}-Q3`
            } else if (month >= 9 && month <= 11) {
                currentQuarter = `${year}-Q4`
            }
            
            // Проверяем, что квартал не раньше Q4 2025
            const [qYear, qNum] = currentQuarter.split('-Q')
            const quarterNum = parseInt(qNum)
            
            if (qYear < minYear || (qYear == minYear && quarterNum < minQuarter)) {
                return '2025-Q4' // Возвращаем минимальный доступный квартал
            }
            
            return currentQuarter || '2025-Q4'
        },
        switchChartTab(tab) {
            this.activeChartTab = tab
        },
        loadChartData() {
            this.loadEfficiencyChartData()
            this.loadMarginalityChartData()
        },
        loadEfficiencyChartData() {
            this.efficiencyChartLoading = true
            
            const params = {
                quarter: this.periodValue,
                department_id: 3,
                user_id: this.userId
            }
            
            axios.get(API_MOTIVATION2025_CHARTS_EFFICIENCY, { params })
                .then(response => {
                    if (response.data.success) {
                        this.efficiencyChartData = response.data.data
                        // Обновляем ключ после успешной загрузки данных
                        this.$nextTick(() => {
                            this.chartKey++
                            console.log('🔄 Personal efficiency chart key updated to:', this.chartKey)
                        })
                    } else {
                        console.error('API Error:', response.data.message)
                    }
                })
                .catch(error => {
                    console.error('Error loading efficiency chart data:', error)
                })
                .finally(() => {
                    this.efficiencyChartLoading = false
                })
        },
        loadMarginalityChartData() {
            this.marginalityChartLoading = true
            
            const params = {
                quarter: this.periodValue,
                department_id: 3,
                user_id: this.userId
            }
            
            axios.get(API_MOTIVATION2025_CHARTS_MARGINALITY, { params })
                .then(response => {
                    if (response.data.success) {
                        this.marginalityChartData = response.data.data
                        // Обновляем ключ после успешной загрузки данных
                        this.$nextTick(() => {
                            this.chartKey++
                            console.log('🔄 Personal marginality chart key updated to:', this.chartKey)
                        })
                    } else {
                        console.error('API Error:', response.data.message)
                    }
                })
                .catch(error => {
                    console.error('Error loading marginality chart data:', error)
                })
                .finally(() => {
                    this.marginalityChartLoading = false
                })
        },
        showOrdersModal(type) {
            // Получаем данные о заказах из API
            this.loadOrdersData(type)
        },
        loadOrdersData(type) {
            const params = {
                quarter: this.periodValue,
                department_id: 3,
                user_id: this.userId
            }
            
            axios.get(API_DEPARTMENT_EFFICIENCY_DATA, { params })
                .then(response => {
                    if (response.data.success && response.data.data.length > 0) {
                        // Находим данные текущего пользователя
                        const userData = response.data.data.find(user => user.employee_id === this.userId)
                        if (userData) {
                            let orders = []
                            let title = ''
                            
                            switch (type) {
                                case 'successful':
                                    orders = userData.successful_orders || []
                                    title = `Успешные поставки - ${userData.employee_name}`
                                    break
                                case 'rejected':
                                    // Для отказных заявок нужно вычислить разность
                                    const allOrders = userData.all_orders || []
                                    const successfulOrders = userData.successful_orders || []
                                    orders = allOrders.filter(order => 
                                        !successfulOrders.some(successful => successful.order_id === order.order_id)
                                    )
                                    title = `Отказные заявки - ${userData.employee_name}`
                                    break
                                case 'all':
                                    orders = userData.all_orders || []
                                    title = `Все заявки - ${userData.employee_name}`
                                    break
                            }
                            
                            if (orders.length === 0) {
                                this.$bvToast.toast('Заказы не найдены', {
                                    title: 'Информация',
                                    variant: 'info',
                                    solid: true
                                })
                                return
                            }
                            
                            this.selectedOrders = orders
                            this.ordersModalTitle = title
                            this.showOrderList = true
                        }
                    } else {
                        this.$bvToast.toast('Данные не найдены', {
                            title: 'Ошибка',
                            variant: 'warning',
                            solid: true
                        })
                    }
                })
                .catch(error => {
                    console.error('Error loading orders data:', error)
                    this.$bvToast.toast('Ошибка загрузки данных', {
                        title: 'Ошибка',
                        variant: 'danger',
                        solid: true
                    })
                })
        },
        resetOrdersModal() {
            this.selectedOrders = []
            this.showOrderList = false
            this.ordersModalTitle = ''
        },
        loadDetailedCalculations() {
            this.detailedCalculationsLoading = true
            
            // Загружаем детальные расчеты напрямую из API
            this.loadDetailedCalculationsFromAPI()
        },
        async loadDetailedCalculationsFromAPI() {
            try {
                const response = await axios.get(API_MOTIVATION2025_DETAILED_CALCULATIONS, {
                    params: {
                        quarter: this.periodValue,
                        user_id: this.userId
                    }
                })
                
                if (response.data.success) {
                    this.detailedCalculations = response.data.data || []
                } else {
                    console.error('Ошибка API:', response.data.message)
                    this.detailedCalculations = []
                }
            } catch (error) {
                console.error('Ошибка при загрузке детальных расчетов:', error)
                this.detailedCalculations = []
            } finally {
                this.detailedCalculationsLoading = false
            }
        },
        openOrderInNewWindow(orderId) {
            // Открываем заказ в новом окне/вкладке
            const routeData = this.$router.resolve({
                path: '/orders',
                query: { order: orderId }
            })
            window.open(routeData.href, '_blank')
        },
        loadMarginBreakdown() {
            // Загружаем расшифровку маржинальности при открытии блока
            // Используем watch для отслеживания изменений
            if (!this.marginBreakdownWatcher) {
                this.marginBreakdownWatcher = this.$watch('showMarginBreakdown', (newVal) => {
                    if (newVal && this.marginBreakdown.length === 0) {
                        this.loadMarginBreakdownFromAPI()
                    }
                })
            }
        },
        async loadMarginBreakdownFromAPI() {
            this.marginBreakdownLoading = true
            try {
                const response = await axios.get(API_MOTIVATION2025_DETAILED_CALCULATIONS, {
                    params: {
                        quarter: this.periodValue,
                        user_id: this.userId
                    }
                })
                
                if (response.data.success) {
                    this.marginBreakdown = response.data.data || []
                } else {
                    console.error('Ошибка API:', response.data.message)
                    this.marginBreakdown = []
                }
            } catch (error) {
                console.error('Ошибка при загрузке расшифровки маржинальности:', error)
                this.marginBreakdown = []
            } finally {
                this.marginBreakdownLoading = false
            }
        }
    }
}
</script>

<style scoped>
.metric-card {
    display: flex;
    align-items: center;
    padding: 1rem;
    border: 1px solid #dee2e6;
    border-radius: 0.5rem;
    background-color: #f8f9fa;
    height: 100%;
}

.metric-icon {
    font-size: 2rem;
    margin-right: 1rem;
}

.metric-content {
    flex: 1;
}

.metric-value {
    font-size: 1.5rem;
    font-weight: bold;
    margin-bottom: 0.25rem;
}

.metric-label {
    font-size: 0.875rem;
    color: #6c757d;
}

.stat-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border-bottom: 1px solid #f8f9fa;
}

.stat-item:last-child {
    border-bottom: none;
}

.stat-label {
    font-weight: 500;
    color: #495057;
}

.stat-value {
    font-weight: bold;
}


.table th {
    border-top: none;
    font-weight: 600;
    color: #495057;
    font-size: 0.875rem;
}

.table td {
    font-size: 0.875rem;
}

/* Chart styles */
.chart-container {
    position: relative;
    height: 400px;
}

.chart-wrapper {
    position: relative;
    height: 100%;
}

.nav-tabs .nav-link {
    border: none;
    border-bottom: 2px solid transparent;
    color: #6c757d;
    font-weight: 500;
}

.nav-tabs .nav-link:hover {
    border-color: transparent;
    border-bottom-color: #dee2e6;
    color: #495057;
}

.nav-tabs .nav-link.active {
    color: #0D6247;
    border-bottom-color: #0D6247;
    background-color: transparent;
}

.nav-tabs .nav-link i {
    font-size: 0.875rem;
}

/* Стили для персональной мотивации */
.motivation-header .card {
    border-left: 4px solid #0D6247;
}

.key-metrics .card {
    border: none;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.metric-value {
    margin-top: 1rem;
}

.bonus-table .table th {
    background-color: #f8f9fa;
    border-top: none;
}

.calculation-step {
    text-align: center;
    padding: 1rem;
}

.calculation-result {
    margin-top: 0.5rem;
}

.final-bonus {
    background-color: #f8f9fa;
    padding: 1.5rem;
    border-radius: 0.5rem;
    margin-top: 1rem;
}

.tip-item {
    padding: 1rem;
    border-left: 3px solid #0D6247;
    margin-bottom: 1rem;
}

/* Стили для таблицы расчета эффективности */
.efficiency-calculation-table .table th {
    background-color: #f8f9fa;
    border-top: none;
    font-weight: 600;
    color: #495057;
}

.efficiency-calculation-table .table td {
    vertical-align: middle;
}

.efficiency-calculation-table .badge {
    font-size: 0.75rem;
    margin-right: 0.25rem;
}

.efficiency-calculation-table .table-success td {
    background-color: #d4edda !important;
    border-color: #c3e6cb !important;
}

.efficiency-calculation-table .table-info td {
    background-color: #d1ecf1 !important;
    border-color: #bee5eb !important;
}

/* Стили для кликабельных цифр */
.clickable-number {
    cursor: pointer;
    text-decoration: underline;
    transition: all 0.1s ease;
}

.clickable-number:hover {
    color: #0056b3 !important;
    text-decoration: none;
    font-weight: bold;
}

/* Жирное выделение для ссылок с заказами */
.clickable-number.has-orders {
    font-weight: bold;
    font-size: 1.05rem;
}

.clickable-number.has-orders:hover {
    font-weight: 900;
}

/* Стили для таблицы расчета маржинальности */
.marginality-calculation-table .table th {
    background-color: #f8f9fa;
    border-top: none;
    font-weight: 600;
    color: #495057;
}

.marginality-calculation-table .table td {
    vertical-align: middle;
}

.marginality-calculation-table .badge {
    font-size: 0.75rem;
}

/* Стили для кликабельных ссылок в таблице маржинальности */
.sidebar-link {
    cursor: pointer;
    text-decoration: underline;
    transition: all 0.1s ease;
}

.sidebar-link:hover {
    color: #0056b3 !important;
    text-decoration: none;
    font-weight: bold;
}

.cursor-pointer {
    cursor: pointer;
}

/* Стили для таблицы детальных расчетов с пагинацией */
.detailed-calculations-table {
    margin-bottom: 0;
}

.detailed-calculations-table .table th {
    background-color: #f8f9fa;
    border-top: none;
    font-weight: 600;
    color: #495057;
    font-size: 0.875rem;
}

.detailed-calculations-table .table td {
    vertical-align: middle;
    font-size: 0.875rem;
}

.detailed-calculations-table .badge {
    font-size: 0.75rem;
}

/* Стили для пагинации */
.pagination {
    margin-bottom: 0;
}

.pagination .page-link {
    color: #0D6247;
    border-color: #dee2e6;
}

.pagination .page-link:hover {
    color: #0D6247;
    background-color: #e9ecef;
    border-color: #dee2e6;
}

.pagination .page-item.active .page-link {
    background-color: #0D6247;
    border-color: #0D6247;
}

.pagination .page-item.disabled .page-link {
    color: #6c757d;
    background-color: #fff;
    border-color: #dee2e6;
}

/* Стили для таблицы расшифровки маржинальности */
.margin-breakdown-table .table th {
    background-color: #f8f9fa;
    border-top: none;
    font-weight: 600;
    color: #495057;
    font-size: 0.875rem;
}

.margin-breakdown-table .table td {
    vertical-align: middle;
    font-size: 0.875rem;
}

.cbr-info-cell {
    font-size: 0.8rem;
    line-height: 1.4;
}

.cbr-info-cell .small {
    font-size: 0.75rem;
}

.margin-breakdown-details-table .table th {
    background-color: #f8f9fa;
    border-top: none;
    font-weight: 600;
    color: #495057;
    font-size: 0.8rem;
}

.margin-breakdown-details-table .table td {
    vertical-align: middle;
    font-size: 0.8rem;
}
</style>
